import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import {
  INearVendor,
  IVendor,
  IVendorDetail,
  IVendorLocation,
} from "Shared/Types/vendor";
import {
  addVendor,
  createComment,
  deleteComment,
  getNearestVendors,
  getNearestVendorsMore,
  getVehicleVendorsList,
  getVendor,
  getVendorsList,
  getVendorsLocations,
  removeVendor,
  updateVendor,
  updateVendorDocuments,
} from "./reducer";

interface InitialState {
  error?: string | null;
  loading: boolean;
  isFuncLoading: boolean;
  isFuncDeleteCommentLoading: boolean;
  success?: boolean | null;
  vendorsList: IVendor[];
  vehicleVendorsList: INearVendor[];
  nearestVendors: {
    list: INearVendor[];
    next: boolean | null;
    loading: boolean;
    error?: string | null;
  };
  vendor: IVendorDetail | null;
  numPage: number;
  vendorsLocations: {
    list: IVendorLocation[];
    next: Record<string, string>[];
    loading: boolean;
    error?: string | null;
    alreadyLoaded: Record<string, true>;
  };
}

const initialState: InitialState = {
  loading: false,
  isFuncLoading: false,
  isFuncDeleteCommentLoading: false,
  error: null,
  vendorsList: [],
  vehicleVendorsList: [],
  nearestVendors: {
    list: [],
    next: null,
    loading: false,
  },
  vendor: null,
  numPage: 0,
  vendorsLocations: {
    list: [],
    next: [],
    loading: false,
    alreadyLoaded: {},
  },
};

const vendorSlice = createSlice({
  name: "vendors",
  initialState,
  reducers: {
    clear(state) {
      state.error = null;
      state.loading = false;
      state.success = null;
    },
    clearCurrentVendor(state) {
      state.vendor = null;
    },
    clearNearestVendors(state) {
      state.nearestVendors = initialState.nearestVendors;
    },
    clearNextVendors(state) {
      state.vendorsLocations.next = [];
    },
    filterVendorsLocationsByClassification(state, action) {
      state.vendorsLocations.list = state.vendorsLocations.list.filter(
        (item) => {
          if (item.classification === action.payload) {
            return true;
          } else {
            delete state.vendorsLocations.alreadyLoaded[item.id];
            return false;
          }
        }
      );
      state.vendorsLocations.next = [];
    },
    clearVendors(state) {
      state.vendorsLocations = initialState.vendorsLocations;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getVendorsList.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.vendorsList = action.payload.results;
      state.numPage = action.payload.count;
    });
    builder.addCase(getVendorsList.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(getVendorsList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    builder.addCase(getVendorsLocations.fulfilled, (state, action) => {
      state.vendorsLocations.loading = false;
      state.vendorsLocations.error = null;
      action.payload.results.forEach((item: IVendorLocation) => {
        if (!state.vendorsLocations.alreadyLoaded[item.id]) {
          state.vendorsLocations.list.push(item);
          state.vendorsLocations.alreadyLoaded[item.id] = true;
        }
      });
      if (action.payload.next) {
        const searchParamsNext = new URLSearchParams(action.payload.next);
        const params: Record<string, string> = {};
        for (const [key, value] of searchParamsNext.entries()) {
          if (key.startsWith("http")) {
            params[key.split("?")[1]] = value;
          } else params[key] = value;
        }
        state.vendorsLocations.next.push(params);
        state.vendorsLocations.next = [...state.vendorsLocations.next];
        state.vendorsLocations.list = [...state.vendorsLocations.list];
      }
    });
    builder.addCase(getVendorsLocations.pending, (state) => {
      state.vendorsLocations.loading = true;
      state.vendorsLocations.error = null;
    });
    builder.addCase(getVendorsLocations.rejected, (state, action) => {
      state.vendorsLocations.loading = false;
      state.vendorsLocations.error = action.error.message;
    });

    builder.addCase(getNearestVendors.fulfilled, (state, action) => {
      state.nearestVendors.loading = false;
      state.nearestVendors.error = null;
      state.nearestVendors.list = action.payload.results;
      state.nearestVendors.next = !!action.payload.next;
    });
    builder.addCase(getNearestVendors.pending, (state) => {
      state.nearestVendors.loading = true;
      state.nearestVendors.error = null;
    });
    builder.addCase(getNearestVendors.rejected, (state, action) => {
      state.nearestVendors.loading = false;
      state.nearestVendors.error = action.error.message;
    });
    builder.addCase(getNearestVendorsMore.fulfilled, (state, action) => {
      state.nearestVendors.loading = false;
      state.nearestVendors.error = null;
      state.nearestVendors.list = [
        ...state.nearestVendors.list,
        ...action.payload.results,
      ];
      state.nearestVendors.next = !!action.payload.next;
    });
    builder.addCase(getNearestVendorsMore.pending, (state) => {
      state.nearestVendors.loading = true;
      state.nearestVendors.error = null;
    });
    builder.addCase(getNearestVendorsMore.rejected, (state, action) => {
      state.nearestVendors.loading = false;
      state.nearestVendors.error = action.error.message;
    });
    builder.addCase(getVehicleVendorsList.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.vehicleVendorsList = action.payload.results;
    });
    builder.addCase(getVehicleVendorsList.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(getVehicleVendorsList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    builder.addCase(getVendor.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.vendor = action.payload;
    });
    builder.addCase(getVendor.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(getVendor.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
    builder.addCase(updateVendorDocuments.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(updateVendorDocuments.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(updateVendorDocuments.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    builder.addCase(addVendor.pending, (state) => {
      state.isFuncLoading = true;
    });
    builder.addCase(addVendor.fulfilled, (state) => {
      state.isFuncLoading = false;
    });
    builder.addCase(addVendor.rejected, (state, action) => {
      state.isFuncLoading = false;
    });
    builder.addCase(updateVendor.pending, (state) => {
      state.isFuncLoading = true;
    });
    builder.addCase(updateVendor.fulfilled, (state) => {
      state.isFuncLoading = false;
    });
    builder.addCase(updateVendor.rejected, (state, action) => {
      state.isFuncLoading = false;
    });

    builder.addCase(createComment.pending, (state) => {
      state.isFuncLoading = true;
    });
    builder.addCase(createComment.fulfilled, (state) => {
      state.isFuncLoading = false;
    });
    builder.addCase(createComment.rejected, (state, action) => {
      state.isFuncLoading = false;
    });
  },
});

export const {
  clear,
  clearCurrentVendor,
  clearNearestVendors,
  clearNextVendors,
  filterVendorsLocationsByClassification,
  clearVendors,
} = vendorSlice.actions;

export const selectVendorList = (state: RootState) => state.vendors.vendorsList;
export const selectVehicleVendorList = (state: RootState) =>
  state.vendors.vehicleVendorsList;
export const selectVendor = (state: RootState) => state.vendors.vendor;
export const selectVendorLoading = (state: RootState) => state.vendors.loading;
export const selectVendorIsFuncLoading = (state: RootState) =>
  state.vendors.isFuncLoading;
export const selectVendorError = (state: RootState) => state.vendors.error;
export const selectVendorSuccess = (state: RootState) => state.vendors.success;
export const selectNearestVendors = (state: RootState) =>
  state.vendors.nearestVendors;
export const selectVendorsLocations = (state: RootState) =>
  state.vendors.vendorsLocations;
export default vendorSlice;
